/**
 * Project Cost Control strip on Construction Execution — REUSE project summary APIs; deep-link only.
 */
import React from 'react';
import { Alert, Button, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd';
import {
  AccountBookOutlined,
  BarChartOutlined,
  DollarOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

type Props = { projectId: string };

export const ConstructionCostControlStrip: React.FC<Props> = ({ projectId }) => {
  const summaryQ = useQuery({
    queryKey: ['cep-cost-control-summary', projectId],
    queryFn: () => projectService.getCostControlSummary(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const exceedancesQ = useQuery({
    queryKey: ['cep-budget-exceedances', projectId],
    queryFn: () => projectService.listBudgetExceedances(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const summary = (summaryQ.data || {}) as Record<string, unknown>;
  const active = !!summary.active;
  const exceedances = exceedancesQ.data || [];
  const openExceedances = exceedances.filter((e) =>
    ['Submitted', 'Approved'].includes(String((e as Record<string, unknown>).status || ''))
  ).length;

  const budgeted = Number(summary.budgeted) || 0;
  const committed = Number(summary.committed) || 0;
  const actual = Number(summary.actual) || 0;
  const available = Number(summary.available) || 0;

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space wrap>
          <AccountBookOutlined />
          <span>Project Cost Control</span>
          {active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
          {openExceedances > 0 ? <Tag color="orange">{openExceedances} exceedance(s)</Tag> : null}
        </Space>
      }
      extra={
        <Space wrap size="small">
          <Link to={`/prj/projects/${projectId}/budgets`}>
            <Button size="small" icon={<AccountBookOutlined />}>
              Budget workbench
            </Button>
          </Link>
          <Link to={`/prj/projects/${projectId}/budgets/variance`}>
            <Button size="small" type="primary" icon={<BarChartOutlined />}>
              Budget vs Actual
            </Button>
          </Link>
          <Link to={`/prj/projects/${projectId}/costs`}>
            <Button size="small" icon={<DollarOutlined />}>
              Project costs
            </Button>
          </Link>
          <Link to="/prj/timesheets">
            <Button size="small" icon={<FieldTimeOutlined />}>
              Timesheets
            </Button>
          </Link>
        </Space>
      }
    >
      {!active && !summaryQ.isLoading ? (
        <Alert
          type="info"
          showIcon
          message="Cost control inactive for this project"
          description="Approve an Original or Current Project Cost Budget to enable availability checks, commitments, and Budget vs Actual. CEP does not own a parallel budget."
        />
      ) : (
        <Row gutter={[16, 8]}>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="Budget" value={budgeted} formatter={(v) => formatMoney(Number(v))} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="Committed"
              value={committed}
              valueStyle={committed > 0 ? { color: '#1677ff' } : undefined}
              formatter={(v) => formatMoney(Number(v))}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="Actual" value={actual} formatter={(v) => formatMoney(Number(v))} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="Available"
              value={available}
              valueStyle={available < 0 ? { color: '#cf1322' } : undefined}
              formatter={(v) => formatMoney(Number(v))}
            />
          </Col>
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Owned by Project domain (Current Budget + Cost Control). Exceedance authorizes
              overspend without rewriting Current Budget.
            </Text>
          </Col>
        </Row>
      )}
    </Card>
  );
};

export default ConstructionCostControlStrip;
